import { differenceInCalendarDays } from 'date-fns';
import subDays from 'date-fns/subDays';
import parseISO from 'date-fns/parseISO';
import min from 'date-fns/min';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';

import { Beneficiary, Stay } from './common';
import { PricePromotion, PriceRange, Prices } from './types/price';
import { Quotation, BeneficiaryQuotation } from './types/quote';

export type InvoicedStay = {
  stay: Stay;
  invoice: Quotation;
  refunds: Quotation[];
};
export interface Coverage extends Stay {
  area: string;
}
export interface CompletedStay extends Stay {
  area: string;
  endDate: string;
}

interface BestCoverageTransition {
  completedStay: CompletedStay;
  stay: Stay;
}

const selectPriceRange = (
  age: number,
  prices: PriceRange[],
  defaultPrices: PriceRange[]
): PriceRange => {
  const doSelectPriceRange = (
    age: number,
    prices: PriceRange[]
  ): PriceRange | undefined => {
    return prices
      .filter(
        priceRange =>
          priceRange.start <= age &&
          age <= (priceRange.end || Number.MAX_SAFE_INTEGER)
      )
      .sort((a, b) => a.price - b.price)[0];
  };
  return (
    (prices && doSelectPriceRange(age, prices)) ||
    doSelectPriceRange(age, defaultPrices)
  );
};

const quoteBeneficiary = (
  beneficiary: Beneficiary,
  days: number,
  selectedPrice: PriceRange[],
  defaultPrices: PriceRange[],
  promotion?: PricePromotion
): BeneficiaryQuotation => {
  const age = Beneficiary.getAge(beneficiary);
  const priceRange = selectPriceRange(age, selectedPrice, defaultPrices);
  if (!priceRange) {
    throw new Error(
      `Unable to find price for ${JSON.stringify(beneficiary)} (${age})
      - prices
      ${JSON.stringify(selectedPrice)},
      - default
      ${JSON.stringify(defaultPrices)},
      - promotion
      ${JSON.stringify(promotion)})`
    );
  }
  const price = Math.min(
    Math.max(priceRange.price * days, priceRange.minPrice || 0),
    priceRange.maxPrice || Number.MAX_SAFE_INTEGER
  );
  return {
    beneficiary,
    price: price,
    details: {
      age: age,
      days: days,
      priceRange: priceRange
    },
    promotion: promotion
  };
};
const areaOf = (country: string, prices: Prices): string => {
  return prices.overrides[country] ? prices.overrides[country].zone : 'B';
};

function quoteBeneficiaries(
  beneficiaries: Beneficiary[],
  area: string,
  nbDays: number,
  countryPrice: PriceRange[],
  defaultPrices: PriceRange[],
  promotions: PricePromotion[]
): BeneficiaryQuotation[] {
  return beneficiaries.map((beneficiary, i) => {
    const beneficiaryNumber = ++i;
    const promotionsAvailable: PricePromotion[] = promotions.filter(
      promotion => {
        const validMinBeneficiaries =
          promotion.minBeneficiaries <= beneficiaryNumber;
        const age = Beneficiary.getAge(beneficiary);
        const currentBeneficiaryNotAdult = age <= promotion.maxAge;
        return validMinBeneficiaries && currentBeneficiaryNotAdult;
      }
    );
    const bestPromotion: PricePromotion = promotionsAvailable.length
      ? promotionsAvailable.reduce((a, b) =>
          a.zones[area][0].price < b.zones[area][0].price ? a : b
        )
      : null;
    const selectedPrice: PriceRange[] = bestPromotion
      ? bestPromotion.zones[area]
      : countryPrice;
    return quoteBeneficiary(
      beneficiary,
      nbDays,
      selectedPrice,
      defaultPrices,
      bestPromotion
    );
  });
}

const quote = (
  stay: Stay,
  beneficiaries: Beneficiary[],
  prices: Prices
): Quotation => {
  const defaultPrices: PriceRange[] = prices.zones.B;
  const area = areaOf(stay.destination, prices);
  const countryPrice: PriceRange[] = prices.zones[area];
  const startDate = parseISO(stay.startDate);
  const endOfFirstWeek: Date = addDays(startDate, 6);
  const endDate: Date = stay.endDate ? parseISO(stay.endDate) : endOfFirstWeek;
  const pricingPeriodStart = startDate;
  const pricingPeriodEnd = min([endOfFirstWeek, endDate]);
  const days =
    differenceInCalendarDays(pricingPeriodEnd, pricingPeriodStart) + 1;

  const beneficiaryQuotations: BeneficiaryQuotation[] = quoteBeneficiaries(
    beneficiaries,
    area,
    days,
    countryPrice,
    defaultPrices,
    prices.promotions
  );
  const total: number = beneficiaryQuotations.reduce(
    (acc, price) => acc + price.price,
    0
  );
  return {
    total: total,
    days: days,
    pricingPeriodStart: format(pricingPeriodStart, 'yyyy-MM-dd'),
    pricingPeriodEnd: format(pricingPeriodEnd, 'yyyy-MM-dd'),
    details: beneficiaryQuotations,
    zone: area
  };
};

function refundOverlapCoverage(
  beneficiaries: Beneficiary[],
  startDate: Date,
  coverage: Coverage,
  countryPrice: PriceRange[],
  defaultPrices: PriceRange[],
  promotions: PricePromotion[]
): Quotation {
  if (coverage.endDate) {
    const endDate = parseISO(coverage.endDate);
    if (endDate >= startDate) {
      const days = differenceInCalendarDays(endDate, startDate) + 1;
      const details = quoteBeneficiaries(
        beneficiaries,
        coverage.area,
        days,
        countryPrice,
        defaultPrices,
        promotions
      );

      const total: number = details.reduce(
        (acc, price) => acc + price.price,
        0
      );
      return {
        total: total,
        pricingPeriodStart: format(startDate, 'yyyy-MM-dd'),
        pricingPeriodEnd: format(endDate, 'yyyy-MM-dd'),
        details: details,
        days,
        zone: coverage.area
      };
    }
    return null;
  }
  return null;
}
function repriceQuotation(quotation: Quotation, prices: Prices): Quotation {
  const defaultPrices: PriceRange[] = prices.zones.B;
  const area = quotation.zone;
  const countryPrice: PriceRange[] = prices.zones[area];
  const days = quotation.days;
  const beneficiaries = quotation.details.map(d => d.beneficiary);
  const beneficiaryQuotations: BeneficiaryQuotation[] = quoteBeneficiaries(
    beneficiaries,
    area,
    days,
    countryPrice,
    defaultPrices,
    prices.promotions
  );
  const total: number = beneficiaryQuotations.reduce(
    (acc, price) => acc + price.price,
    0
  );
  return {
    ...quotation,
    details: beneficiaryQuotations,
    total
  };
}
function reprice(invoicedStay: InvoicedStay, prices: Prices): InvoicedStay {
  return {
    ...invoicedStay,
    invoice: repriceQuotation(invoicedStay.invoice, prices),
    refunds: invoicedStay.refunds.map(refund =>
      repriceQuotation(refund, prices)
    )
  };
}

function invoice(
  stay: CompletedStay,
  coverage: Coverage,
  beneficiaries: Beneficiary[],
  prices: Prices,
  referenceDate: string
): InvoicedStay {
  const defaultPrices: PriceRange[] = prices.zones.B;
  const area = stay.area;
  const countryPrice: PriceRange[] = prices.zones[area];
  const today = parseISO(referenceDate);
  const startDate = parseISO(stay.startDate);
  const endDate = parseISO(stay.endDate);
  const days = differenceInCalendarDays(endDate, startDate) + 1;
  const beneficiaryQuotations: BeneficiaryQuotation[] = quoteBeneficiaries(
    beneficiaries,
    area,
    days,
    countryPrice,
    defaultPrices,
    prices.promotions
  );

  const total: number = beneficiaryQuotations.reduce(
    (acc, price) => acc + price.price,
    0
  );
  const refundableStartDate =
    startDate <= today
      ? format(startDate, 'yyyy-MM-dd')
      : format(today, 'yyyy-MM-dd');
  const refund =
    coverage.area > area
      ? refundOverlapCoverage(
          beneficiaries,
          parseISO(refundableStartDate),
          coverage,
          prices.zones[coverage.area],
          defaultPrices,
          prices.promotions
        )
      : null;
  const e =
    endDate >= startDate ? format(endDate, 'yyyy-MM-dd') : refundableStartDate;
  return {
    stay: {
      ...stay,
      startDate: refundableStartDate,
      endDate: e
    },
    invoice: {
      total: total,
      days: days,
      pricingPeriodStart: stay.startDate,
      pricingPeriodEnd: stay.endDate,
      details: beneficiaryQuotations,
      zone: area
    },
    refunds: refund ? [refund] : []
  };
}

function completeStay(
  currentStay: Stay,
  coverage: Coverage,
  destination: string,
  prices: Prices,
  referenceDate: string
): BestCoverageTransition {
  const now = parseISO(referenceDate);
  const destinationArea = areaOf(destination, prices);

  const yesterday = format(subDays(now, 1), 'yyyy-MM-dd');
  const today = format(now, 'yyyy-MM-dd');
  const tomorrow = format(addDays(now, 1), 'yyyy-MM-dd');
  let transitionDate = yesterday;
  let startDate = today;

  if (destinationArea > coverage.area) {
    transitionDate = yesterday;
    startDate = today;
  } else if (destinationArea === coverage.area) {
    if (coverage.endDate) {
      transitionDate = coverage.endDate;
      startDate = format(addDays(parseISO(transitionDate), 1), 'yyyy-MM-dd');
    } else {
      transitionDate = yesterday;
      startDate = today;
    }
  } else {
    transitionDate = today;
    startDate = tomorrow;
  }
  return {
    completedStay: {
      ...currentStay,

      endDate: transitionDate,
      area: areaOf(currentStay.destination, prices)
    },
    stay: {
      destination: destination,
      startDate: startDate
    }
  };
}
const updateQuotationPeriod = (
  quotation: Quotation,
  periodStart: string,
  periodEnd: string
): Quotation => {
  const startDate =
    quotation.pricingPeriodStart >= periodStart
      ? quotation.pricingPeriodStart
      : periodStart;
  const endDate =
    quotation.pricingPeriodEnd <= periodEnd
      ? quotation.pricingPeriodEnd
      : periodEnd;
  return {
    ...quotation,
    pricingPeriodStart: startDate,
    pricingPeriodEnd: endDate,
    days: differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) + 1
  };
};

const updatePeriod = (
  leg: InvoicedStay,
  periodStart: string,
  periodEnd: string
): InvoicedStay => {
  const refunds = leg.refunds.map(refund =>
    updateQuotationPeriod(refund, periodStart, periodEnd)
  );
  return {
    stay: leg.stay,
    invoice: updateQuotationPeriod(leg.invoice, periodStart, periodEnd),
    refunds
  };
};
const total = (invoicedStay: InvoicedStay): number => {
  return (
    invoicedStay.invoice.total -
    invoicedStay.refunds.reduce((acc, refund) => acc + refund.total, 0)
  );
};
const Pricer = {
  quote: quote,
  selectPriceRange,
  areaOf,
  completeStay,
  invoice,
  reprice,
  repriceQuotation,
  total,
  updatePeriod,
  updateQuotationPeriod,
  getAge: Beneficiary.getAge
};
export default Pricer;
export {
  quote,
  selectPriceRange,
  areaOf,
  completeStay,
  invoice,
  reprice,
  repriceQuotation,
  total,
  updatePeriod,
  updateQuotationPeriod
};
export * from './common';
export * from './types/price';
export * from './types/quote';
